import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Ruler, Plus, Trash2, Camera, Upload, Image as ImageIcon } from 'lucide-react';
import { ProjectMeasurement, AddProjectMeasurementParams, UpdateProjectMeasurementParams, calculateArea, getCustomFieldValue, getCustomFieldUnit } from '../lib/projectMeasurements';
import { getMeasurementPresets, addMeasurementPreset, deleteMeasurementPreset, MeasurementPreset, MeasurementField } from '../lib/measurementPresets';
import { uploadMeasurementPhoto, getMeasurementPhotos, deleteMeasurementPhoto, getMeasurementPhotoUrl, type MeasurementPhoto } from '../services/measurementPhotoService';
import CameraCapture from './CameraCapture';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
interface AddMeasurementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (measurement: AddProjectMeasurementParams | UpdateProjectMeasurementParams) => Promise<void>;
  measurement?: ProjectMeasurement | null;
  /** Owning project. Provide either projectId or fieldQuoteId. */
  projectId?: string;
  /** Owning field quote. Provide either projectId or fieldQuoteId. */
  fieldQuoteId?: string;
  businessId: string;
}

const UNIT_OPTIONS = [
  { value: 'ft', label: 'Feet (ft)' },
  { value: 'in', label: 'Inches (in)' },
  { value: 'm', label: 'Meters (m)' },
  { value: 'cm', label: 'Centimeters (cm)' },
  { value: 'mm', label: 'Millimeters (mm)' }
];

type StandardDimensionKey = 'length' | 'width' | 'height' | 'area';

const STANDARD_DIMENSIONS: Array<{
  key: StandardDimensionKey;
  label: string;
  helper?: string;
  unitKey: 'length_unit' | 'width_unit' | 'height_unit' | 'area_unit';
}> = [
  { key: 'length', label: 'Length', unitKey: 'length_unit' },
  { key: 'width', label: 'Width', unitKey: 'width_unit' },
  { key: 'height', label: 'Height', unitKey: 'height_unit' },
  { key: 'area', label: 'Area', unitKey: 'area_unit', helper: 'Auto-calc from length × width' }
];

// Helper function to convert fractional strings to decimal numbers
// Handles formats like: "1 1/4", "1/2", "3/4", "1.5", "2", etc.
const parseMeasurementValue = (value: string): number | null => {
  if (!value || value.trim() === '') return null;
  
  const trimmed = value.trim();
  
  // Try to parse as a simple number first
  const simpleNumber = parseFloat(trimmed);
  if (!isNaN(simpleNumber) && !trimmed.includes('/')) {
    return simpleNumber;
  }
  
  // Handle fractional formats like "1 1/4" or "1/2"
  // Match patterns like: "1 1/4", "1/2", "3/4", etc.
  const fractionPattern = /^(\d+)?\s*(\d+)\/(\d+)$/;
  const match = trimmed.match(fractionPattern);
  
  if (match) {
    const wholePart = match[1] ? parseFloat(match[1]) : 0;
    const numerator = parseFloat(match[2]);
    const denominator = parseFloat(match[3]);
    
    if (denominator !== 0) {
      return wholePart + (numerator / denominator);
    }
  }
  
  // If it doesn't match fraction pattern, try parsing as decimal
  const decimal = parseFloat(trimmed);
  return isNaN(decimal) ? null : decimal;
};

export default function AddMeasurementModal({
  isOpen,
  onClose,
  onSave,
  measurement,
  projectId,
  fieldQuoteId,
  businessId
}: AddMeasurementModalProps) {
  const [view, setView] = useState<'presets' | 'form'>('presets'); // 'presets' or 'form'
  const [presets, setPresets] = useState<MeasurementPreset[]>([]);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [name, setName] = useState('');
  const [length, setLength] = useState<string>('');
  const [width, setWidth] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [area, setArea] = useState<string>('');
  const [unit, setUnit] = useState<string>('ft');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [autoCalculateArea, setAutoCalculateArea] = useState(true);
  const [isCreatingPreset, setIsCreatingPreset] = useState(false);
  const [isStandardDimensionDropdownOpen, setIsStandardDimensionDropdownOpen] = useState(false);
  const [presetFormData, setPresetFormData] = useState({
    name: '',
    has_length: false,
    has_width: false,
    has_height: false,
    has_area: false,
    length_unit: 'in', // Unit for length dimension
    width_unit: 'in',  // Unit for width dimension
    height_unit: 'in', // Unit for height dimension
    area_unit: 'in',   // Unit for area dimension
    unit: 'in', // Keep for backward compatibility/fallback
    notes: '',
    custom_fields: [] as MeasurementField[]
  });
  const [selectedPreset, setSelectedPreset] = useState<MeasurementPreset | null>(null);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [additionalCustomFields, setAdditionalCustomFields] = useState<MeasurementField[]>([]); // Custom fields added to this specific measurement
  const [photos, setPhotos] = useState<MeasurementPhoto[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState<string[]>([]);
  const [showCamera, setShowCamera] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<MeasurementPhoto | null>(null);
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([]); // Store files to upload after measurement is saved
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const isEditing = !!measurement;

  // Debug: Log pendingPhotos changes
  useEffect(() => {
    console.log('pendingPhotos changed:', pendingPhotos.length, pendingPhotos.map(f => f.name));
  }, [pendingPhotos]);

  // Reset all state when modal closes
  const resetModalState = () => {
    setView('presets');
    setName('');
    setLength('');
    setWidth('');
    setHeight('');
    setArea('');
    setUnit('ft');
    setNotes('');
    setCustomFieldValues({});
    setAdditionalCustomFields([]);
    setAutoCalculateArea(true);
    setIsCreatingPreset(false);
    setSelectedPreset(null);
    setPresetFormData({
      name: '',
      has_length: false,
      has_width: false,
      has_height: false,
      has_area: false,
      length_unit: 'in',
      width_unit: 'in',
      height_unit: 'in',
      area_unit: 'in',
      unit: 'in',
      notes: '',
      custom_fields: []
    });
    setIsSaving(false);
    setPhotos([]);
    setUploadingPhotos([]);
    setShowCamera(false);
    setSelectedPhoto(null);
    setPendingPhotos([]);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      console.log('No files selected');
      return;
    }

    console.log('Files selected:', files.length, 'isEditing:', isEditing, 'measurement?.id:', measurement?.id);

    // If editing, upload photos immediately
    if (isEditing && measurement?.id) {
      for (const file of Array.from(files)) {
        try {
          setUploadingPhotos(prev => [...prev, file.name]);
          const result = await uploadMeasurementPhoto({
            measurementId: measurement.id,
            file
          });
          if (result.success && result.photo) {
            setPhotos(prev => [result.photo!, ...prev]);
            toast.success(`Photo "${file.name}" uploaded successfully`);
          } else {
            toast.error(`Failed to upload ${file.name}: ${result.error || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error uploading photo:', error);
          toast.error(`Failed to upload ${file.name}`);
        } finally {
          setUploadingPhotos(prev => prev.filter(name => name !== file.name));
        }
      }
    } else {
      // For new measurements, store files to upload after measurement is saved
      const fileArray = Array.from(files);
      console.log('Adding pending photos:', fileArray.length, 'Current pendingPhotos:', pendingPhotos.length);
      
      // Use functional update to ensure we get the latest state
      setPendingPhotos(prev => {
        const updated = [...prev, ...fileArray];
        console.log('Updated pendingPhotos from', prev.length, 'to', updated.length);
        return updated;
      });
      
      toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} added (will upload after saving)`);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Use setTimeout to ensure the click happens after any modal animations/renders
    setTimeout(() => {
      if (fileInputRef.current) {
        try {
          // Force click on the file input
          fileInputRef.current.click();
        } catch (error) {
          console.error('Error triggering file input:', error);
          toast.error('Failed to open file picker');
        }
      } else {
        console.error('File input ref is not set');
        toast.error('File input not available. Please try again.');
      }
    }, 100);
  };

  const handleCameraCapture = () => {
    setShowCamera(true);
  };

  const handleCameraPhotos = async (photoBlobs: string[]) => {
    setShowCamera(false);
    
    // Convert blob URLs to Files
    const files: File[] = [];
    for (const blobUrl of photoBlobs) {
      try {
        const response = await fetch(blobUrl);
        const blob = await response.blob();
        const file = new File([blob], `camera-${Date.now()}-${Math.random().toString(36).substring(2)}.jpg`, { type: 'image/jpeg' });
        files.push(file);
      } catch (error) {
        console.error('Error converting blob to file:', error);
        toast.error('Failed to process camera photo');
      }
    }

    if (files.length === 0) return;

    // If editing, upload photos immediately
    if (isEditing && measurement?.id) {
      for (const file of files) {
        try {
          setUploadingPhotos(prev => [...prev, file.name]);
          const result = await uploadMeasurementPhoto({
            measurementId: measurement.id,
            file
          });
          if (result.success && result.photo) {
            setPhotos(prev => [result.photo!, ...prev]);
            toast.success(`Photo uploaded successfully`);
          } else {
            toast.error(`Failed to upload photo: ${result.error || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error uploading photo:', error);
          toast.error(`Failed to upload photo`);
        } finally {
          setUploadingPhotos(prev => prev.filter(name => name !== file.name));
        }
      }
    } else {
      // For new measurements, store files to upload after measurement is saved
      setPendingPhotos(prev => [...prev, ...files]);
      toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} captured (will upload after saving)`);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
  };

  const removePendingPhoto = (index: number) => {
    setPendingPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeletePhoto = async (photo: MeasurementPhoto) => {
    try {
      const success = await deleteMeasurementPhoto(photo.id);
      if (success) {
        setPhotos(prev => prev.filter(p => p.id !== photo.id));
        toast.success('Photo deleted successfully');
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    }
  }, [isOpen]);

  // Load presets when modal opens (only for new measurements)
  useEffect(() => {
    if (isOpen && !isEditing && businessId) {
      loadPresets();
      setView('presets');
    } else if (isEditing && isOpen) {
      // When editing, go straight to form
      setView('form');
    }
  }, [isOpen, isEditing, businessId]);

  const loadPresets = async () => {
    if (!businessId) return;
    
    setIsLoadingPresets(true);
    try {
      const businessPresets = await getMeasurementPresets(businessId);
      setPresets(businessPresets);
    } catch (error) {
      console.error('Error loading measurement presets:', error);
      // Don't show error toast - just continue with empty presets
    } finally {
      setIsLoadingPresets(false);
    }
  };

  const handleDeletePreset = async (presetId: string, presetName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the preset select
    
    if (!window.confirm(`Are you sure you want to delete the preset "${presetName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteMeasurementPreset(presetId);
      await loadPresets(); // Refresh the list
      toast.success('Preset deleted successfully');
    } catch (error) {
      console.error('Error deleting preset:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete preset');
    }
  };

  const handlePresetSelect = (preset: MeasurementPreset | 'custom') => {
    if (preset === 'custom') {
      // Reset form and show custom form (sandbox mode - start with just name)
      setSelectedPreset(null);
      setName('');
      setLength('');
      setWidth('');
      setHeight('');
      setArea('');
      setUnit('ft');
      setNotes('');
      setCustomFieldValues({});
      setAdditionalCustomFields([]); // Start with no fields - user adds them
      setAutoCalculateArea(true);
      setView('form');
    } else {
      // Store selected preset and populate form with preset structure
      setSelectedPreset(preset);
      setName(preset.name);
      setUnit(preset.unit);
      setNotes(preset.notes || '');
      // Clear dimension values - user will fill them in
      setLength('');
      setWidth('');
      setHeight('');
      setArea('');
      setAutoCalculateArea(true);
      // Initialize custom field values
      const customValues: Record<string, string> = {};
      if (preset.custom_fields && preset.custom_fields.length > 0) {
        preset.custom_fields.forEach(field => {
          if (field.name !== 'length' && field.name !== 'width' && field.name !== 'height' && field.name !== 'area') {
            customValues[field.name] = '';
          }
        });
      }
      setCustomFieldValues(customValues);
      setAdditionalCustomFields([]); // Reset additional fields when selecting a preset
      setView('form');
    }
  };

  const addAdditionalCustomField = () => {
    const fieldName = `custom_${Date.now()}`;
    setAdditionalCustomFields([
      ...additionalCustomFields,
      { name: fieldName, label: '', type: 'number', required: false, calculate: false, unit: 'in' } // Default to 'in' for new fields
    ]);
  };

  const updateAdditionalCustomField = (index: number, field: Partial<MeasurementField>) => {
    const updated = [...additionalCustomFields];
    updated[index] = { ...updated[index], ...field };
    setAdditionalCustomFields(updated);
  };

  const removeAdditionalCustomField = (index: number) => {
    const field = additionalCustomFields[index];
    setAdditionalCustomFields(additionalCustomFields.filter((_, i) => i !== index));
    // Remove the value from customFieldValues
    const newValues = { ...customFieldValues };
    delete newValues[field.name];
    setCustomFieldValues(newValues);
  };

  const handleCreatePreset = () => {
    // Reset preset form and show it
    setPresetFormData({
      name: '',
      has_length: false,
      has_width: false,
      has_height: false,
      has_area: false,
      length_unit: 'in',
      width_unit: 'in',
      height_unit: 'in',
      area_unit: 'in',
      unit: 'in',
      notes: '',
      custom_fields: []
    });
    setIsCreatingPreset(true);
  };

  const isStandardDimensionSelected = (key: StandardDimensionKey) => {
    switch (key) {
      case 'length':
        return presetFormData.has_length;
      case 'width':
        return presetFormData.has_width;
      case 'height':
        return presetFormData.has_height;
      case 'area':
        return presetFormData.has_area;
      default:
        return false;
    }
  };

  const setStandardDimensionSelected = (key: StandardDimensionKey, selected: boolean) => {
    setPresetFormData(prev => {
      const updated = { ...prev };
      if (key === 'length') updated.has_length = selected;
      if (key === 'width') updated.has_width = selected;
      if (key === 'height') updated.has_height = selected;
      if (key === 'area') updated.has_area = selected;
      return updated;
    });
  };

  const getStandardDimensionUnit = (key: StandardDimensionKey) => {
    switch (key) {
      case 'length':
        return presetFormData.length_unit;
      case 'width':
        return presetFormData.width_unit;
      case 'height':
        return presetFormData.height_unit;
      case 'area':
        return presetFormData.area_unit;
      default:
        return 'in';
    }
  };

  const setStandardDimensionUnit = (key: StandardDimensionKey, value: string) => {
    setPresetFormData(prev => {
      const updated = { ...prev };
      if (key === 'length') updated.length_unit = value;
      if (key === 'width') updated.width_unit = value;
      if (key === 'height') updated.height_unit = value;
      if (key === 'area') updated.area_unit = value;
      return updated;
    });
  };

  const addCustomField = () => {
    const defaultLabel = 'Custom';
    const fieldName = defaultLabel.toLowerCase();
    setPresetFormData({
      ...presetFormData,
      custom_fields: [
        ...presetFormData.custom_fields,
        { name: fieldName, label: defaultLabel, type: 'number', required: false, calculate: false, unit: 'ft' }
      ]
    });
  };

  const updateCustomField = (index: number, field: Partial<MeasurementField>) => {
    const updated = [...presetFormData.custom_fields];
    updated[index] = { ...updated[index], ...field };
    setPresetFormData({ ...presetFormData, custom_fields: updated });
  };

  const removeCustomField = (index: number) => {
    setPresetFormData({
      ...presetFormData,
      custom_fields: presetFormData.custom_fields.filter((_, i) => i !== index)
    });
  };

  const handleSavePreset = async () => {
    if (!presetFormData.name.trim()) {
      toast.error('Preset name is required');
      return;
    }

    // Build custom_fields array from standard fields and custom fields
    const customFields: MeasurementField[] = [];
    
    // Add standard fields if enabled, each with its own unit
    if (presetFormData.has_length) {
      customFields.push({ name: 'length', label: 'Length', type: 'number', required: true, calculate: false, unit: presetFormData.length_unit });
    }
    if (presetFormData.has_width) {
      customFields.push({ name: 'width', label: 'Width', type: 'number', required: true, calculate: false, unit: presetFormData.width_unit });
    }
    if (presetFormData.has_height) {
      customFields.push({ name: 'height', label: 'Height', type: 'number', required: true, calculate: false, unit: presetFormData.height_unit });
    }
    if (presetFormData.has_area) {
      customFields.push({ name: 'area', label: 'Area', type: 'number', required: false, calculate: true, unit: presetFormData.area_unit });
    }
    
    // Add custom fields (validate they have name and label)
    for (const field of presetFormData.custom_fields) {
      const fieldLabel = field.label.trim();
      const rawName = (field.name || '').trim() || fieldLabel;
      if (fieldLabel) {
        // Ensure name is lowercase and no spaces (for JSON key)
        const fieldName = rawName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        customFields.push({
          name: fieldName,
          label: fieldLabel,
          type: field.type,
          required: field.required,
          calculate: field.calculate || false,
          unit: field.unit || presetFormData.unit
        });
      }
    }

    // At least one field must be defined
    if (customFields.length === 0) {
      toast.error('Please add at least one field (standard dimension or custom field)');
      return;
    }

    try {
      const newPreset = await addMeasurementPreset({
        business_id: businessId,
        name: presetFormData.name.trim(),
        has_length: presetFormData.has_length,
        has_width: presetFormData.has_width,
        has_height: presetFormData.has_height,
        has_area: presetFormData.has_area,
        custom_fields: customFields,
        unit: presetFormData.unit,
        notes: presetFormData.notes.trim() || null,
      });

      // Refresh presets list
      await loadPresets();
      
      // Select the newly created preset
      handlePresetSelect(newPreset);
      
      setIsCreatingPreset(false);
      toast.success('Preset created successfully');
    } catch (error) {
      console.error('Error creating preset:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create preset');
    }
  };

  // Find matching preset for a measurement
  // Matches based on which fields have values in the measurement
  const findMatchingPreset = (measurement: ProjectMeasurement, presetList: MeasurementPreset[]): MeasurementPreset | null => {
    if (!measurement || presetList.length === 0) return null;

    // Get field names that have values in the measurement
    const measurementFields = new Set<string>();
    if (measurement.length !== null && measurement.length !== undefined) measurementFields.add('length');
    if (measurement.width !== null && measurement.width !== undefined) measurementFields.add('width');
    if (measurement.height !== null && measurement.height !== undefined) measurementFields.add('height');
    if (measurement.area !== null && measurement.area !== undefined) measurementFields.add('area');
    
    // Add custom fields that have values
    if (measurement.custom_fields) {
      Object.entries(measurement.custom_fields).forEach(([key, value]) => {
        if (key !== 'length' && key !== 'width' && key !== 'height' && key !== 'area' && 
            value !== null && value !== undefined && value !== '') {
          measurementFields.add(key);
        }
      });
    }

    // Try to find a preset that matches the measurement's field structure
    for (const preset of presetList) {
      // Check if preset has custom_fields defined
      if (preset.custom_fields && preset.custom_fields.length > 0) {
        // Get field names from preset (excluding area if it's auto-calculated)
        const presetFieldsSet = new Set(
          preset.custom_fields
            .filter(f => f.name !== 'area' || !f.calculate) // Exclude auto-calculated area
            .map(f => f.name)
        );
        
        // Check if all preset fields (except auto-calculated area) exist in measurement
        // We want an exact match: measurement should have exactly the fields the preset defines
        const presetFieldsArray = Array.from(presetFieldsSet);
        const hasAllPresetFields = presetFieldsArray.length > 0 && 
          presetFieldsArray.every(field => measurementFields.has(field));
        
        // Check if measurement doesn't have extra fields (except area which might be auto-calculated)
        const measurementFieldsArray = Array.from(measurementFields);
        const hasNoExtraFields = measurementFieldsArray.every(field => 
          presetFieldsSet.has(field) || field === 'area'
        );
        
        // Also check unit matches
        const unitMatches = preset.unit === measurement.unit;

        if (hasAllPresetFields && hasNoExtraFields && unitMatches) {
          return preset;
        }
      } else {
        // Fallback to legacy boolean flags matching
        const presetFields = new Set<string>();
        if (preset.has_length) presetFields.add('length');
        if (preset.has_width) presetFields.add('width');
        if (preset.has_height) presetFields.add('height');
        // Don't include area in matching for legacy presets since it might be auto-calculated
        // if (preset.has_area) presetFields.add('area');
        
        const hasCustomFields = measurement.custom_fields && Object.keys(measurement.custom_fields).length > 0;

        // If measurement has custom fields, skip legacy presets
        if (hasCustomFields) continue;

        // Check if all preset fields exist in measurement and unit matches
        const presetFieldsArray = Array.from(presetFields);
        const hasAllPresetFields = presetFieldsArray.length > 0 && 
          presetFieldsArray.every(field => measurementFields.has(field));
        const unitMatches = preset.unit === measurement.unit;

        if (hasAllPresetFields && unitMatches) {
          return preset;
        }
      }
    }

    return null;
  };

  // Load photos for a measurement
  const loadMeasurementPhotos = async (measurementId: string) => {
    try {
      const measurementPhotos = await getMeasurementPhotos(measurementId);
      setPhotos(measurementPhotos);
    } catch (error) {
      console.error('Error loading measurement photos:', error);
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (measurement && isOpen) {
      setName(measurement.name);
      setLength(measurement.length?.toString() || '');
      setWidth(measurement.width?.toString() || '');
      setHeight(measurement.height?.toString() || '');
      setArea(measurement.area?.toString() || '');
      setUnit(measurement.unit);
      setNotes(measurement.notes || '');
      // Populate custom field values
      if (measurement.custom_fields) {
        const customValues: Record<string, string> = {};
        Object.entries(measurement.custom_fields).forEach(([key, value]) => {
          // Handle both old format (direct value) and new format ({ value, unit })
          const displayValue = getCustomFieldValue(value);
          customValues[key] = displayValue?.toString() || '';
        });
        // Also populate standard fields if they exist
        if (measurement.length !== null && measurement.length !== undefined) {
          customValues['length'] = measurement.length.toString();
        }
        if (measurement.width !== null && measurement.width !== undefined) {
          customValues['width'] = measurement.width.toString();
        }
        if (measurement.height !== null && measurement.height !== undefined) {
          customValues['height'] = measurement.height.toString();
        }
        if (measurement.area !== null && measurement.area !== undefined) {
          customValues['area'] = measurement.area.toString();
        }
        setCustomFieldValues(customValues);
      } else {
        // Populate standard fields if they exist
        const customValues: Record<string, string> = {};
        if (measurement.length !== null && measurement.length !== undefined) {
          customValues['length'] = measurement.length.toString();
        }
        if (measurement.width !== null && measurement.width !== undefined) {
          customValues['width'] = measurement.width.toString();
        }
        if (measurement.height !== null && measurement.height !== undefined) {
          customValues['height'] = measurement.height.toString();
        }
        if (measurement.area !== null && measurement.area !== undefined) {
          customValues['area'] = measurement.area.toString();
        }
        setCustomFieldValues(customValues);
      }
      setAutoCalculateArea(false); // Don't auto-calculate when editing

      // Load photos for this measurement
      if (measurement.id) {
        loadMeasurementPhotos(measurement.id);
      }

      // Try to find matching preset and set it
      // Load presets first if not already loaded
      if (presets.length === 0 && businessId) {
        loadPresets().then(() => {
          // After presets are loaded, find matching preset
          getMeasurementPresets(businessId).then(loadedPresets => {
            const matchingPreset = findMatchingPreset(measurement, loadedPresets);
            if (matchingPreset) {
              setSelectedPreset(matchingPreset);
              
              // When editing with a preset, check if measurement has fields not in the preset
              // and add them as additionalCustomFields so they display
              if (measurement.custom_fields) {
                const presetFieldNames = new Set<string>();
                if (matchingPreset.custom_fields && matchingPreset.custom_fields.length > 0) {
                  matchingPreset.custom_fields.forEach(f => presetFieldNames.add(f.name));
                } else {
                  // Legacy preset - check boolean flags
                  if (matchingPreset.has_length) presetFieldNames.add('length');
                  if (matchingPreset.has_width) presetFieldNames.add('width');
                  if (matchingPreset.has_height) presetFieldNames.add('height');
                  if (matchingPreset.has_area) presetFieldNames.add('area');
                }
                
                // Find custom fields in measurement that aren't in preset
                const additionalFields: MeasurementField[] = [];
                Object.entries(measurement.custom_fields).forEach(([key, value]) => {
                  // Skip standard fields (length, width, height, area) - they're handled separately
                  if (!['length', 'width', 'height', 'area'].includes(key) && !presetFieldNames.has(key)) {
                    // Create a field definition for display
                    additionalFields.push({
                      name: key,
                      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                      type: typeof value === 'number' ? 'number' : 'text',
                      required: false,
                      calculate: false,
                      unit: unit // Use measurement's unit as default
                    });
                  }
                });
                
                if (additionalFields.length > 0) {
                  setAdditionalCustomFields(additionalFields);
                }
              }
            } else {
              // No preset matched - this is a custom measurement
              // Extract all fields from measurement.custom_fields as additionalCustomFields
              // Also check if there are standard fields and convert them to custom fields for display
              const additionalFields: MeasurementField[] = [];
              
              // Add standard fields as custom fields if they exist
              if (measurement.length !== null && measurement.length !== undefined) {
                additionalFields.push({
                  name: 'length',
                  label: 'Length',
                  type: 'number',
                  required: false,
                  calculate: false,
                  unit: unit
                });
                setLength(measurement.length.toString());
              }
              if (measurement.width !== null && measurement.width !== undefined) {
                additionalFields.push({
                  name: 'width',
                  label: 'Width',
                  type: 'number',
                  required: false,
                  calculate: false,
                  unit: unit
                });
                setWidth(measurement.width.toString());
              }
              if (measurement.height !== null && measurement.height !== undefined) {
                additionalFields.push({
                  name: 'height',
                  label: 'Height',
                  type: 'number',
                  required: false,
                  calculate: false,
                  unit: unit
                });
                setHeight(measurement.height.toString());
              }
              if (measurement.area !== null && measurement.area !== undefined) {
                additionalFields.push({
                  name: 'area',
                  label: 'Area',
                  type: 'number',
                  required: false,
                  calculate: false,
                  unit: unit
                });
                setArea(measurement.area.toString());
              }
              
              // Add custom fields from measurement.custom_fields
              if (measurement.custom_fields) {
                Object.entries(measurement.custom_fields).forEach(([key, value]) => {
                  // Skip if already added as standard field
                  if (!['length', 'width', 'height', 'area'].includes(key)) {
                    const fieldUnit = getCustomFieldUnit(value, unit);
                    additionalFields.push({
                      name: key,
                      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                      type: typeof getCustomFieldValue(value) === 'number' ? 'number' : 'text',
                      required: false,
                      calculate: false,
                      unit: fieldUnit || unit
                    });
                  }
                });
              }
              
              setAdditionalCustomFields(additionalFields);
            }
          });
        });
      } else {
        // Presets already loaded, find matching preset
        const matchingPreset = findMatchingPreset(measurement, presets);
        if (matchingPreset) {
          setSelectedPreset(matchingPreset);
          
          // When editing with a preset, check if measurement has fields not in the preset
          // and add them as additionalCustomFields so they display
          if (measurement.custom_fields) {
            const presetFieldNames = new Set<string>();
            if (matchingPreset.custom_fields && matchingPreset.custom_fields.length > 0) {
              matchingPreset.custom_fields.forEach(f => presetFieldNames.add(f.name));
            } else {
              // Legacy preset - check boolean flags
              if (matchingPreset.has_length) presetFieldNames.add('length');
              if (matchingPreset.has_width) presetFieldNames.add('width');
              if (matchingPreset.has_height) presetFieldNames.add('height');
              if (matchingPreset.has_area) presetFieldNames.add('area');
            }
            
            // Find custom fields in measurement that aren't in preset
            const additionalFields: MeasurementField[] = [];
            Object.entries(measurement.custom_fields).forEach(([key, value]) => {
              // Skip standard fields (length, width, height, area) - they're handled separately
              if (!['length', 'width', 'height', 'area'].includes(key) && !presetFieldNames.has(key)) {
                // Create a field definition for display
                additionalFields.push({
                  name: key,
                  label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                  type: typeof value === 'number' ? 'number' : 'text',
                  required: false,
                  calculate: false,
                  unit: unit // Use measurement's unit as default
                });
              }
            });
            
            if (additionalFields.length > 0) {
              setAdditionalCustomFields(additionalFields);
            }
          }
        } else {
          // No preset matched - this is a custom measurement
          // Extract all fields from measurement.custom_fields as additionalCustomFields
          // Also check if there are standard fields and convert them to custom fields for display
          const additionalFields: MeasurementField[] = [];
          
          // Add standard fields as custom fields if they exist
          if (measurement.length !== null && measurement.length !== undefined) {
            additionalFields.push({
              name: 'length',
              label: 'Length',
              type: 'number',
              required: false,
              calculate: false,
              unit: unit
            });
            setLength(measurement.length.toString());
          }
          if (measurement.width !== null && measurement.width !== undefined) {
            additionalFields.push({
              name: 'width',
              label: 'Width',
              type: 'number',
              required: false,
              calculate: false,
              unit: unit
            });
            setWidth(measurement.width.toString());
          }
          if (measurement.height !== null && measurement.height !== undefined) {
            additionalFields.push({
              name: 'height',
              label: 'Height',
              type: 'number',
              required: false,
              calculate: false,
              unit: unit
            });
            setHeight(measurement.height.toString());
          }
          if (measurement.area !== null && measurement.area !== undefined) {
            additionalFields.push({
              name: 'area',
              label: 'Area',
              type: 'number',
              required: false,
              calculate: false,
              unit: unit
            });
            setArea(measurement.area.toString());
          }
          
          // Add custom fields from measurement.custom_fields
          if (measurement.custom_fields) {
            Object.entries(measurement.custom_fields).forEach(([key, value]) => {
              // Skip if already added as standard field
              if (!['length', 'width', 'height', 'area'].includes(key)) {
                const fieldUnit = getCustomFieldUnit(value, unit);
                additionalFields.push({
                  name: key,
                  label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                  type: typeof getCustomFieldValue(value) === 'number' ? 'number' : 'text',
                  required: false,
                  calculate: false,
                  unit: fieldUnit || unit
                });
              }
            });
          }
          
          setAdditionalCustomFields(additionalFields);
        }
      }
    } else if (!measurement && isOpen) {
      // Reset photos for new measurement
      setPhotos([]);
    }
  }, [measurement, isOpen, businessId, presets]);

  // Auto-calculate area when length/width changes (only if area is part of the preset)
  useEffect(() => {
    if (autoCalculateArea && length && width && selectedPreset) {
      // Check if area is part of the preset
      const hasArea = selectedPreset.custom_fields?.some(f => f.name === 'area' && f.calculate) || 
                     (selectedPreset.has_area && !selectedPreset.custom_fields);
      
      if (hasArea) {
        const lengthNum = parseMeasurementValue(length);
        const widthNum = parseMeasurementValue(width);
        if (lengthNum !== null && widthNum !== null && lengthNum > 0 && widthNum > 0) {
          const calculatedArea = calculateArea(lengthNum, widthNum);
          if (calculatedArea !== null) {
            setArea(calculatedArea.toString());
          }
        }
      }
    } else if (!selectedPreset && autoCalculateArea && length && width) {
      // No preset selected (custom measurement) - allow auto-calculation
      const lengthNum = parseMeasurementValue(length);
      const widthNum = parseMeasurementValue(width);
      if (lengthNum !== null && widthNum !== null && lengthNum > 0 && widthNum > 0) {
        const calculatedArea = calculateArea(lengthNum, widthNum);
        if (calculatedArea !== null) {
          setArea(calculatedArea.toString());
        }
      }
    }
  }, [length, width, autoCalculateArea, selectedPreset]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Measurement name is required');
      return;
    }

    // Validate required fields based on selected preset
    if (selectedPreset) {
      const fields = selectedPreset.custom_fields || [];
      
      // Check standard fields
      const hasLength = fields.some(f => f.name === 'length') || selectedPreset.has_length;
      const hasWidth = fields.some(f => f.name === 'width') || selectedPreset.has_width;
      const hasHeight = fields.some(f => f.name === 'height') || selectedPreset.has_height;
      const hasArea = fields.some(f => f.name === 'area') || selectedPreset.has_area;
      
      if (hasLength && !length) {
        toast.error('Length is required for this preset');
        return;
      }
      if (hasWidth && !width) {
        toast.error('Width is required for this preset');
        return;
      }
      if (hasHeight && !height) {
        toast.error('Height is required for this preset');
        return;
      }
      if (hasArea && !area) {
        toast.error('Area is required for this preset');
        return;
      }
      
      // Check custom fields
      for (const field of fields) {
        if (field.name !== 'length' && field.name !== 'width' && field.name !== 'height' && field.name !== 'area') {
          if (field.required && !customFieldValues[field.name]) {
            toast.error(`${field.label} is required for this preset`);
            return;
          }
        }
      }
    }

    setIsSaving(true);
    try {
      // Build custom_fields object from custom field values
      const customFields: Record<string, { value: number | string; unit?: string }> = {};
      if (selectedPreset && selectedPreset.custom_fields) {
        for (const field of selectedPreset.custom_fields) {
          if (field.name !== 'length' && field.name !== 'width' && field.name !== 'height' && field.name !== 'area') {
            const value = customFieldValues[field.name];
            if (value) {
              // Convert to number if field type is number (handles fractions)
              const parsedValue = field.type === 'number' ? (parseMeasurementValue(value) || 0) : value;
              customFields[field.name] = {
                value: parsedValue,
                unit: field.unit // Store the unit with the value!
              };
            }
          }
        }
      }
      
      // Add additional custom fields that were added to this measurement instance
      for (const field of additionalCustomFields) {
        const value = customFieldValues[field.name];
        if (value !== undefined && value !== '') {
          // Use the label as the key, or fall back to the name
          const fieldKey = field.label || field.name;
          const parsedValue = field.type === 'number' ? (parseMeasurementValue(value) || 0) : value;
          customFields[fieldKey] = {
            value: parsedValue,
            unit: field.unit
          };
        }
      }

      // For custom measurements (no preset), only save custom fields, no standard fields
      // For preset measurements, include standard fields if they're part of the preset
      const hasAreaInPreset = selectedPreset ? (
        selectedPreset.custom_fields?.some(f => f.name === 'area') || 
        (!selectedPreset.custom_fields && selectedPreset.has_area)
      ) : false; // Custom measurements don't use standard area field

      const measurementData: AddProjectMeasurementParams | UpdateProjectMeasurementParams = {
        name: name.trim(),
        // Only include standard fields if using a preset
        length: (selectedPreset && length) ? parseMeasurementValue(length) : null,
        width: (selectedPreset && width) ? parseMeasurementValue(width) : null,
        height: (selectedPreset && height) ? parseMeasurementValue(height) : null,
        area: (hasAreaInPreset && area) ? parseMeasurementValue(area) : null,
        custom_fields: Object.keys(customFields).length > 0 ? customFields : null,
        unit: unit || 'ft', // Fallback unit
        notes: notes.trim() || null,
      };

      // Attach the parent (project or field quote) only for new measurements
      if (!isEditing) {
        if (fieldQuoteId) {
          (measurementData as AddProjectMeasurementParams).field_quote_id = fieldQuoteId;
        } else {
          (measurementData as AddProjectMeasurementParams).project_id = projectId;
        }
      }

      // Save measurement first
      await onSave(measurementData);
      
      // Upload pending photos
      let measurementId = measurement?.id;
      
      if (!isEditing && !measurementId && pendingPhotos.length > 0) {
        // For new measurements, wait a moment for the database to commit, then fetch the measurement
        // This matches the measurement we just created
        await new Promise(resolve => setTimeout(resolve, 500)); // Small delay to ensure DB commit
        
        try {
          const { data: { user } } = await supabase.auth.getUser();
          let lookup = supabase
            .from('project_measurements')
            .select('id');
          lookup = fieldQuoteId
            ? lookup.eq('field_quote_id', fieldQuoteId)
            : lookup.eq('project_id', projectId as string);
          const { data: measurements, error: fetchError } = await lookup
            .eq('name', name.trim())
            .eq('unit', unit)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();
          
          if (!fetchError && measurements) {
            measurementId = measurements.id;
          }
        } catch (error) {
          console.error('Error fetching measurement ID:', error);
          // If we can't get the ID, we'll skip photo uploads for this measurement
          // User can add photos later by editing the measurement
        }
      }
      
      // Upload pending photos if we have a measurement ID
      if (measurementId && pendingPhotos.length > 0) {
        for (const file of pendingPhotos) {
          try {
            setUploadingPhotos(prev => [...prev, file.name]);
            const result = await uploadMeasurementPhoto({
              measurementId: measurementId,
              file
            });
            if (result.success && result.photo) {
              // Photo uploaded successfully
            } else {
              toast.error(`Failed to upload ${file.name}`);
            }
          } catch (error) {
            console.error('Error uploading photo:', error);
            toast.error(`Failed to upload ${file.name}`);
          } finally {
            setUploadingPhotos(prev => prev.filter(name => name !== file.name));
          }
        }
        setPendingPhotos([]);
      }
      
      toast.success(isEditing ? 'Measurement updated successfully' : 'Measurement added successfully');
      resetModalState();
      onClose();
    } catch (error) {
      console.error('Error saving measurement:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save measurement');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 100100,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          opacity: 1,
          pointerEvents: 'auto'
        }}
        onClick={() => {
          resetModalState();
          onClose();
        }}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none sm:items-center" style={{ zIndex: 100110 }}>
        <div 
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60">
            <div className="flex items-center gap-2">
              <Ruler className="h-5 w-5 text-gray-600" />
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Measurement' : 'Add Measurement'}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing && view === 'presets' && !isCreatingPreset && (
                <button
                  onClick={handleCreatePreset}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-4 w-4" />
                  Add Template
                </button>
              )}
              <button
                onClick={() => {
                  resetModalState();
                  onClose();
                }}
                className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          {isCreatingPreset ? (
            <div className="flex-1 overflow-y-auto bg-gray-50/30">
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                <p className="text-sm text-gray-600 mb-4">Create a new measurement preset:</p>
                
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={presetFormData.name}
                    onChange={(e) => setPresetFormData({ ...presetFormData, name: e.target.value })}
                    placeholder="e.g., Standard Window, Standard Door"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                {/* Standard Dimensions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Standard Dimensions
                  </label>
                  <div className="space-y-2">
                    {STANDARD_DIMENSIONS.filter(dimension => isStandardDimensionSelected(dimension.key)).map(dimension => (
                      <div key={dimension.key} className="p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-700">{dimension.label}</div>
                            {dimension.helper && (
                              <div className="text-xs text-gray-500 mt-0.5">{dimension.helper}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={getStandardDimensionUnit(dimension.key)}
                              onChange={(e) => setStandardDimensionUnit(dimension.key, e.target.value)}
                              className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              {UNIT_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => setStandardDimensionSelected(dimension.key, false)}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                              aria-label={`Remove ${dimension.label}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {presetFormData.custom_fields.map((field, index) => (
                      <div key={`custom-${index}`} className="p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) => {
                                const label = e.target.value;
                                const normalized = label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                                updateCustomField(index, {
                                  label,
                                  name: normalized || field.name
                                });
                              }}
                              placeholder="Custom field"
                              className="w-full text-sm font-medium text-gray-700 bg-transparent border-b border-gray-200 focus:border-blue-500 focus:outline-none"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            {field.type === 'number' && (
                              <select
                                value={field.unit || 'ft'}
                                onChange={(e) => updateCustomField(index, { unit: e.target.value })}
                                className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                {UNIT_OPTIONS.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            )}
                            <button
                              type="button"
                              onClick={() => removeCustomField(index)}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                              aria-label="Remove custom field"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsStandardDimensionDropdownOpen(prev => !prev)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-[10px] text-[13px] font-semibold text-gray-700 hover:text-gray-900 hover:border-gray-400 hover:bg-gray-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Plus className="h-4 w-4" />
                        Add Dimension
                      </button>
                      {isStandardDimensionDropdownOpen && (
                        <div className="absolute z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                          {STANDARD_DIMENSIONS.filter(dimension => !isStandardDimensionSelected(dimension.key)).length === 0 ? (
                            <div className="px-3 py-2 text-xs text-gray-500">No standard dimensions available</div>
                          ) : (
                            STANDARD_DIMENSIONS.filter(dimension => !isStandardDimensionSelected(dimension.key)).map(dimension => (
                              <button
                                key={dimension.key}
                                type="button"
                                onClick={() => {
                                  setStandardDimensionSelected(dimension.key, true);
                                  setIsStandardDimensionDropdownOpen(false);
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <div className="flex items-center justify-between">
                                  <span>{dimension.label}</span>
                                  {dimension.helper && (
                                    <span className="text-xs text-gray-400">{dimension.helper}</span>
                                  )}
                                </div>
                              </button>
                            ))
                          )}
                          <div className="border-t border-gray-200/70" />
                          <button
                            type="button"
                            onClick={() => {
                              addCustomField();
                              setIsStandardDimensionDropdownOpen(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Custom Field
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>

              {/* Footer */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200/60 bg-white flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreatingPreset(false);
                    setPresetFormData({
                      name: '',
                      has_length: false,
                      has_width: false,
                      has_height: false,
                      has_area: false,
                      unit: 'ft',
                      notes: '',
                      custom_fields: []
                    });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePreset}
                  disabled={!presetFormData.name.trim() || (!presetFormData.has_length && !presetFormData.has_width && !presetFormData.has_height && !presetFormData.has_area && presetFormData.custom_fields.length === 0)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Preset
                </button>
              </div>
            </div>
          ) : view === 'presets' ? (
            <div className="flex-1 overflow-y-auto bg-gray-50/30">
              <div className="p-4 sm:p-6">
                <p className="text-sm text-gray-600 mb-4">Select a preset measurement or create a custom one:</p>
                
                {isLoadingPresets ? (
                  <div className="text-center py-8 text-sm text-gray-500">
                    Loading presets...
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Custom Option */}
                    <button
                      onClick={() => handlePresetSelect('custom')}
                      className="w-full p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                          <Plus className="h-5 w-5 text-gray-600 group-hover:text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Custom Measurement</div>
                          <div className="text-sm text-gray-500">Create a new measurement from scratch</div>
                        </div>
                      </div>
                    </button>

                    {/* Preset Options */}
                    {presets.length === 0 ? (
                      <div className="text-center py-8 text-sm text-gray-500">
                        No presets available yet. Use "Custom Measurement" to create one.
                      </div>
                    ) : (
                      presets.map((preset) => (
                        <div
                          key={preset.id}
                          className="w-full bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group relative"
                        >
                          <button
                            onClick={() => handlePresetSelect(preset)}
                            className="w-full p-4 pr-12 text-left"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Ruler className="h-5 w-5 text-blue-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">{preset.name}</div>
                                <div className="text-sm text-gray-500">
                                  <span>
                                    {preset.custom_fields && preset.custom_fields.length > 0 ? (
                                      // Show custom fields
                                      preset.custom_fields.map(f => f.label).join(', ')
                                    ) : (
                                      // Fallback to legacy fields
                                      [
                                        preset.has_length && 'Length',
                                        preset.has_width && 'Width',
                                        preset.has_height && 'Height',
                                        preset.has_area && 'Area'
                                      ].filter(Boolean).join(', ')
                                    )}
                                    {((preset.custom_fields && preset.custom_fields.length > 0) || preset.has_length || preset.has_width || preset.has_height || preset.has_area) ? ` (${preset.unit})` : ''}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                          <button
                            onClick={(e) => handleDeletePreset(preset.id, preset.name, e)}
                            className="absolute top-4 right-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            title="Delete preset"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 bg-gray-50/30">
              {/* Scrollable content — footer stays pinned below */}
              <div className="flex-1 overflow-y-auto">
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Living Room, Window 1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              {/* Unit - Only show for presets, custom measurements use per-field units */}
              {selectedPreset && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit of Measurement
                  </label>
                  <select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    {UNIT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Fields - Dynamically rendered based on preset */}
              {(() => {
                // Get fields to display from preset or show all if no preset
                const fieldsToShow: MeasurementField[] = [];
                
                if (selectedPreset && selectedPreset.custom_fields && selectedPreset.custom_fields.length > 0) {
                  // Use custom_fields from preset
                  fieldsToShow.push(...selectedPreset.custom_fields);
                } else if (selectedPreset) {
                  // Fallback to legacy boolean flags
                  if (selectedPreset.has_length) {
                    fieldsToShow.push({ name: 'length', label: 'Length', type: 'number', required: true, calculate: false });
                  }
                  if (selectedPreset.has_width) {
                    fieldsToShow.push({ name: 'width', label: 'Width', type: 'number', required: true, calculate: false });
                  }
                  if (selectedPreset.has_height) {
                    fieldsToShow.push({ name: 'height', label: 'Height', type: 'number', required: true, calculate: false });
                  }
                  if (selectedPreset.has_area) {
                    fieldsToShow.push({ name: 'area', label: 'Area', type: 'number', required: false, calculate: true });
                  }
                } else {
                  // No preset - Custom measurement mode (sandbox)
                  // When editing, check if measurement has standard fields and only show those
                  if (isEditing && measurement) {
                    if (measurement.length !== null && measurement.length !== undefined) {
                      fieldsToShow.push({ name: 'length', label: 'Length', type: 'number', required: false, calculate: false });
                    }
                    if (measurement.width !== null && measurement.width !== undefined) {
                      fieldsToShow.push({ name: 'width', label: 'Width', type: 'number', required: false, calculate: false });
                    }
                    if (measurement.height !== null && measurement.height !== undefined) {
                      fieldsToShow.push({ name: 'height', label: 'Height', type: 'number', required: false, calculate: false });
                    }
                    if (measurement.area !== null && measurement.area !== undefined) {
                      fieldsToShow.push({ name: 'area', label: 'Area', type: 'number', required: false, calculate: true });
                    }
                  }
                  
                  // For new custom measurements, don't show any default fields
                  // User will add fields using the + button
                }

                // Separate standard fields from custom fields for layout
                const standardFields = fieldsToShow.filter(f => ['length', 'width', 'height'].includes(f.name));
                const areaField = fieldsToShow.find(f => f.name === 'area');
                const customFields = fieldsToShow.filter(f => !['length', 'width', 'height', 'area'].includes(f.name));
                
                // Add additional custom fields that were added to this measurement instance
                const allCustomFields = [...customFields, ...additionalCustomFields];

                const resolveUnitLabel = (unitValue?: string) => {
                  const resolved = unitValue || selectedPreset?.unit || unit;
                  return UNIT_OPTIONS.find(option => option.value === resolved)?.label || resolved;
                };

                const resolvePresetUnit = (fieldName: string) => {
                  if (selectedPreset?.custom_fields && selectedPreset.custom_fields.length > 0) {
                    const presetField = selectedPreset.custom_fields.find(f => f.name === fieldName);
                    if (presetField?.unit) return presetField.unit;
                  }
                  return selectedPreset?.unit || unit;
                };

                return (
                  <>
                    {/* Standard dimensions (length, width, height) */}
                    {standardFields.length > 0 && (
                      <div className="space-y-4">
                        {standardFields.map((field) => {
                          const value = field.name === 'length' ? length : field.name === 'width' ? width : height;
                          const setValue = field.name === 'length' ? setLength : field.name === 'width' ? setWidth : setHeight;
                          const fieldUnit = field.unit || resolvePresetUnit(field.name);
                          
                          return (
                            <div key={field.name}>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {field.label} {resolveUnitLabel(fieldUnit) && `(${resolveUnitLabel(fieldUnit)})`} {field.required && <span className="text-red-500">*</span>}
                              </label>
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder="e.g., 1 1/4 or 1.25"
                                required={field.required}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Area field */}
                    {areaField && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          {(() => {
                            const areaUnit = areaField.unit || resolvePresetUnit('area');
                            return (
                          <label className="block text-sm font-medium text-gray-700">
                            {areaField.label} ({resolveUnitLabel(areaUnit)}²) {areaField.required && <span className="text-red-500">*</span>}
                          </label>
                            );
                          })()}
                          {areaField.calculate && length && width && (
                            <label className="flex items-center gap-2 text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={autoCalculateArea}
                                onChange={(e) => setAutoCalculateArea(e.target.checked)}
                                className="rounded"
                              />
                              Auto-calculate
                            </label>
                          )}
                        </div>
                        <input
                          type="text"
                          value={area}
                          onChange={(e) => {
                            setArea(e.target.value);
                            setAutoCalculateArea(false);
                          }}
                          placeholder="e.g., 1 1/4 or 1.25"
                          required={areaField.required}
                          disabled={autoCalculateArea && areaField.calculate && length && width}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                      </div>
                    )}

                    {/* Custom fields from preset */}
                    {customFields.length > 0 && (
                      <div className="space-y-4">
                        {customFields.map((field) => (
                          <div key={field.name}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {(() => {
                                const fieldUnit = field.unit || resolvePresetUnit(field.name);
                                const unitLabel = field.type === 'number' ? resolveUnitLabel(fieldUnit) : '';
                                return (
                                  <>
                                    {field.label} {unitLabel && `(${unitLabel})`} {field.required && <span className="text-red-500">*</span>}
                                  </>
                                );
                              })()}
                            </label>
                            {field.type === 'number' ? (
                              <input
                                type="text"
                                value={customFieldValues[field.name] || ''}
                                onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.name]: e.target.value })}
                                placeholder="e.g., 1 1/4 or 1.25"
                                required={field.required}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            ) : (
                              <input
                                type="text"
                                value={customFieldValues[field.name] || ''}
                                onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.name]: e.target.value })}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                required={field.required}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Custom Fields Section - For custom measurements (no preset) */}
                    {!selectedPreset && (
                      <div className="space-y-3">
                        {additionalCustomFields.map((field, index) => (
                          <div key={field.name} className="p-3 bg-white border border-gray-200 rounded-lg">
                            {/* Field Header Row: Label | Unit | Remove */}
                            <div className="flex items-center gap-3 mb-3">
                              <input
                                type="text"
                                value={field.label}
                                onChange={(e) => updateAdditionalCustomField(index, { label: e.target.value })}
                                placeholder="Field name"
                                className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <select
                                value={field.unit || 'in'}
                                onChange={(e) => updateAdditionalCustomField(index, { unit: e.target.value })}
                                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                {UNIT_OPTIONS.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => removeAdditionalCustomField(index)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            {/* Value Input */}
                            <input
                              type="text"
                              value={customFieldValues[field.name] || ''}
                              onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.name]: e.target.value })}
                              placeholder="e.g., 1 1/4 or 1.25"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                        ))}
                        
                        {/* Add Field Button - Centered when empty, below fields when fields exist */}
                        <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={addAdditionalCustomField}
                            className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors border-2 border-dashed border-blue-300 hover:border-blue-400"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <Plus className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Additional custom fields (when using a preset) */}
                    {selectedPreset && additionalCustomFields.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-gray-700">
                            Additional Fields
                          </label>
                          {!isEditing && (
                            <button
                              type="button"
                              onClick={addAdditionalCustomField}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                            >
                              <Plus className="h-4 w-4" />
                              Add Field
                            </button>
                          )}
                        </div>
                        <div className="space-y-3">
                          {additionalCustomFields.map((field, index) => (
                            <div key={field.name} className={`${isEditing && !selectedPreset ? 'p-4 bg-white border border-gray-200' : 'p-4 bg-gray-50 border border-gray-200'} rounded-lg space-y-3`}>
                              <div className="flex items-start justify-between gap-2">
                                {/* Only show field label/unit editing when not editing or when adding new fields with preset */}
                                {(!isEditing || selectedPreset) && (
                                  <>
                                    <div className="flex-1 grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                          Field Label
                                        </label>
                                        <input
                                          type="text"
                                          value={field.label}
                                          onChange={(e) => updateAdditionalCustomField(index, { label: e.target.value })}
                                          placeholder="e.g., Glass Thickness"
                                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                      </div>
                                      {field.type === 'number' && (
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">
                                            Unit
                                          </label>
                                          <select
                                            value={field.unit || 'ft'}
                                            onChange={(e) => updateAdditionalCustomField(index, { unit: e.target.value })}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                          >
                                            {UNIT_OPTIONS.map(option => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeAdditionalCustomField(index)}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  {field.label || 'New Field'} {field.unit && `(${UNIT_OPTIONS.find(u => u.value === field.unit)?.label || field.unit})`}
                                </label>
                                {field.type === 'number' ? (
                                  <input
                                    type="text"
                                    value={customFieldValues[field.name] || ''}
                                    onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.name]: e.target.value })}
                                    placeholder="e.g., 1 1/4 or 1.25"
                                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${isEditing && !selectedPreset ? 'bg-white' : ''}`}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={customFieldValues[field.name] || ''}
                                    onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.name]: e.target.value })}
                                    placeholder={`Enter ${field.label.toLowerCase() || 'value'}`}
                                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${isEditing && !selectedPreset ? 'bg-white' : ''}`}
                                  />
                                )}
                              </div>
                              {isEditing && !selectedPreset && (
                                <button
                                  type="button"
                                  onClick={() => removeAdditionalCustomField(index)}
                                  className="mt-2 p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-xs font-medium"
                                >
                                  Remove Field
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </>
                );
              })()}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes about this measurement..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                />
              </div>

              {/* Photos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photos
                </label>
                
                {/* Photo Upload Buttons */}
                <div className="flex flex-col sm:flex-row gap-2.5 mb-3">
                  <button
                    type="button"
                    onClick={handleCameraCapture}
                    disabled={showCamera}
                    className="flex-1 flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Open Camera
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    className="flex-1 flex items-center justify-center px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors text-sm font-medium"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Photos
                  </button>
                </div>

                {/* Hidden file input - Always render when form is visible */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{
                    position: 'absolute',
                    width: '0.1px',
                    height: '0.1px',
                    opacity: 0,
                    overflow: 'hidden',
                    zIndex: -1
                  }}
                  id="measurement-photo-upload"
                />

                {/* Existing Photos */}
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {photos.map((photo) => {
                      const photoUrl = getMeasurementPhotoUrl(photo.file_path);
                      return (
                        <div
                          key={photo.id}
                          className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
                        >
                          <img
                            src={photoUrl}
                            alt={photo.file_name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleDeletePhoto(photo)}
                            className="absolute top-1 right-1 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pending Photos (to be uploaded) */}
                {pendingPhotos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {pendingPhotos.map((file, index) => {
                      const objectUrl = URL.createObjectURL(file);
                      const isUploading = uploadingPhotos.includes(file.name);
                      return (
                        <div
                          key={index}
                          className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
                        >
                          <img
                            src={objectUrl}
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                          {isUploading && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <div className="text-white text-xs">Uploading...</div>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removePendingPhoto(index)}
                            className="absolute top-1 right-1 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {photos.length === 0 && pendingPhotos.length === 0 && (
                  <div className="text-center py-4 text-sm text-gray-500">
                    <ImageIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    No photos added yet
                  </div>
                )}
              </div>
            </div>
              </div>

              {/* Footer — sticky below the scrollable content */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200/60 bg-white flex items-center justify-between flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    if (isEditing) {
                      resetModalState();
                      onClose();
                    } else {
                      setView('presets');
                      setSelectedPreset(null);
                      setName('');
                      setLength('');
                      setWidth('');
                      setHeight('');
                      setArea('');
                      setUnit('ft');
                      setNotes('');
                      setCustomFieldValues({});
                      setAutoCalculateArea(true);
                    }
                  }}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {isEditing ? 'Cancel' : 'Back'}
                </button>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      resetModalState();
                      onClose();
                    }}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving || !name.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : (isEditing ? 'Update' : 'Add')}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Camera Capture Modal */}
      {showCamera && (
        <CameraCapture
          isOpen={showCamera}
          onClose={handleCameraClose}
          onPhotosCapture={handleCameraPhotos}
        />
      )}

    </>,
    document.body
  );
}
